import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { SettingsService } from './settings.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HlrSettings } from '@helpers/types/models';
// import { forkJoin, of, switchMap, tap } from 'rxjs';
import { of, switchMap, tap } from 'rxjs';
import {
	AlarisButtonComponent,
	AlarisCardComponent,
	AlarisCheckboxComponent,
	AlarisDialogService,
	AlarisInputComponent,
	AlarisLoadingDirective,
} from '@campaign-portal/components-library';
import { ImportSettingsDialogComponent } from './dialog/dialog.component';
import { UserService } from '../../../auth/user.service';
import { TranslateModule } from '@ngx-translate/core';
// import { UrSettingsService } from './ur-settings.service';
// import { URSettings } from '@helpers/types/user-and-roles';

@Component({
	selector: 'app-settings',
	templateUrl: './settings.component.html',
	styleUrl: './settings.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AlarisCardComponent,
		AlarisLoadingDirective,
		FormsModule,
		ReactiveFormsModule,
		AlarisInputComponent,
		AlarisCheckboxComponent,
		AlarisButtonComponent,
		TranslateModule,
	],
})
export class SettingsComponent implements OnInit {
	readonly errors: { key: string; value: string }[] = [
		{ key: 'mask', value: 'gl.errors.invalid_format' },
		{ key: 'required', value: 'gl.errors.required' },
	];
	readonly form = new FormGroup({
		settingId: new FormControl<number>(0, { nonNullable: true }),
		cacheStorageLimit: new FormControl<boolean | null>(null),
		enableGlobalProducts: new FormControl<boolean | null>(null),
		enableGlobalSenders: new FormControl<boolean | null>(null),
		apiHost: new FormControl<string | null>(null),
		concurrentRequestTimeout: new FormControl<number>(0, {
			nonNullable: true,
			validators: [Validators.required, Validators.pattern('\\d{0,99}')],
		}),
		defaultConnectionTimeout: new FormControl<number>(0, {
			nonNullable: true,
			validators: [Validators.required, Validators.pattern('\\d{0,99}')],
		}),
		defaultRequestTimeout: new FormControl<number>(0, {
			nonNullable: true,
			validators: [Validators.required, Validators.pattern('\\d{0,99}')],
		}),
		dnisLimit: new FormControl<number>(0, {
			nonNullable: true,
			validators: [Validators.pattern('\\d{0,99}')],
		}),
		// Use2FA: new FormControl(false),
	});

	private readonly destroyRef = inject(DestroyRef);

	constructor(
		public readonly user: UserService,
		public readonly settingsService: SettingsService,
		private readonly dialog: AlarisDialogService,
		// readonly urSettingsService: UrSettingsService
	) {}

	ngOnInit(): void {
		// forkJoin([this.settingsService.read(), this.urSettingsService.read()])
		this.settingsService
			.read()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((settings) => {
				this.form.patchValue({
					...settings,
					// ...urSettings
				});
			});
	}

	update(): void {
		const hlrSettings: HlrSettings = {
			settingId: this.form.controls.settingId.value,
			apiHost: this.form.controls.apiHost.value,
			cacheStorageLimit: this.form.controls.cacheStorageLimit.value,
			concurrentRequestTimeout: Number(this.form.controls.concurrentRequestTimeout.value),
			defaultConnectionTimeout: Number(this.form.controls.defaultConnectionTimeout.value),
			defaultRequestTimeout: Number(this.form.controls.defaultRequestTimeout.value),
			dnisLimit: Number(this.form.controls.dnisLimit.value),
			enableGlobalProducts: this.form.controls.enableGlobalProducts.value,
			enableGlobalSenders: this.form.controls.enableGlobalSenders.value,
		};
		// const urSettings: URSettings = {
		// 	Use2FA: !!this.form.controls.Use2FA.value,
		// };
		// forkJoin([this.settingsService.update(hlrSettings), this.urSettingsService.update(urSettings)])
		this.settingsService
			.update(hlrSettings)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: () => {
					this.form.markAsPristine();
				},
				error: () => {
					this.ngOnInit();
				},
			});
	}

	import(): void {
		this.dialog
			.open<boolean>(ImportSettingsDialogComponent)
			.closed.pipe(
				takeUntilDestroyed(this.destroyRef),
				switchMap((result) => {
					if (result) {
						return this.settingsService.importConfig().pipe(
							takeUntilDestroyed(this.destroyRef),
							switchMap(() => {
								return this.settingsService.exportConfig();
							}),
							tap((resp) => {
								if (resp.reply === 'Success') {
									if (this.form.dirty) {
										this.form.markAsPristine();
									}
								}
							})
						);
					} else {
						return of({ reply: '' });
					}
				})
			)
			.subscribe();
	}
}